import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { ProductoService } from '../../core/services/producto.service';
import { AlertService } from '../../core/services/alert.service';
import { ProductoListApi } from '../../core/models/api/producto-api';
import { UiLoadingComponent } from '../../shared/components/ui-loading.component';
import { UiEmptyImageComponent } from '../../shared/components/ui-empty-image.component';

@Component({
    selector: 'app-admin-productos',
    standalone: true,
    imports: [CommonModule, FormsModule, RouterLink, UiLoadingComponent, UiEmptyImageComponent],
    template: `
    <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
      <div>
        <h2 class="text-2xl font-black text-gray-800">Productos</h2>
        <p class="text-sm text-gray-400 mt-1">{{ total() }} en inventa · selecciona y activa los que quieras publicar en la web</p>
      </div>
    </div>

    <div class="mb-5 flex flex-wrap items-center gap-3">
      <input [ngModel]="busqueda()" (ngModelChange)="onSearch($event)" name="busqueda" placeholder="Buscar por nombre o SKU" class="w-full md:w-72 rounded-2xl border border-gray-200 bg-white px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-rose-200" />
      <select [ngModel]="estado()" (ngModelChange)="onEstado($event)" name="estado" class="rounded-2xl border border-gray-200 bg-white px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-rose-200">
        <option value="todos">Todos</option>
        <option value="sin">Sin activar</option>
        <option value="activados">Activados</option>
      </select>
      <select [ngModel]="tipo()" (ngModelChange)="onTipo($event)" name="tipo" class="rounded-2xl border border-gray-200 bg-white px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-rose-200">
        <option value="todos">Todos los tipos</option>
        <option value="VARIANTE">Productos</option>
        <option value="COMBO">Combos</option>
      </select>
      <select [ngModel]="pageSize()" (ngModelChange)="onPageSize($event)" name="pageSize" class="rounded-2xl border border-gray-200 bg-white px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-rose-200">
        <option [ngValue]="12">12 por página</option>
        <option [ngValue]="24">24 por página</option>
        <option [ngValue]="48">48 por página</option>
      </select>
    </div>

    <div *ngIf="seleccionados().size > 0" class="mb-4 flex flex-wrap items-center gap-3 bg-rose-50 border border-rose-200 rounded-2xl px-4 py-3">
      <span class="text-sm font-bold text-rose-700">{{ seleccionados().size }} seleccionado(s)</span>
      <button type="button" (click)="activarSeleccionados()" [disabled]="procesando()" class="px-4 py-2 rounded-xl text-white text-xs font-bold disabled:opacity-60" style="background-color: var(--bg-main);">
        {{ procesando() ? 'Procesando...' : 'Activar seleccionados' }}
      </button>
      <ng-container *ngIf="!confirmarBulk()">
        <button type="button" (click)="confirmarBulk.set(true)" [disabled]="procesando()" class="px-4 py-2 rounded-xl text-xs font-bold text-gray-600 border border-gray-300 bg-white disabled:opacity-60">
          Desactivar seleccionados
        </button>
      </ng-container>
      <span *ngIf="confirmarBulk()" class="flex items-center gap-2 text-xs font-bold">
        <span class="text-red-500">¿Quitar los seleccionados de la web?</span>
        <button type="button" (click)="desactivarSeleccionados()" class="text-red-500 hover:underline cursor-pointer">Sí, desactivar</button>
        <button type="button" (click)="confirmarBulk.set(false)" class="text-gray-400 hover:underline cursor-pointer">Cancelar</button>
      </span>
      <button type="button" (click)="limpiarSeleccion()" class="text-xs font-bold text-gray-400">Limpiar</button>
    </div>

    <div *ngIf="!loading() && productos().length > 0" class="mb-3 flex items-center gap-2 text-sm text-gray-500">
      <input type="checkbox" [checked]="paginaCompletaSeleccionada()" (change)="togglePagina($any($event.target).checked)" class="accent-rose-500" />
      Seleccionar página
    </div>

    <app-ui-loading *ngIf="loading()"></app-ui-loading>

    <div *ngIf="!loading() && productos().length === 0" class="bg-white rounded-[2rem] border border-rose-100 p-12 text-center text-gray-400">
      No hay productos con ese criterio.
    </div>

    <div *ngIf="!loading()" class="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
      <article *ngFor="let p of productos()" class="bg-white rounded-[1.75rem] border shadow-sm overflow-hidden flex gap-4 p-3" [class.border-rose-300]="seleccionados().has(p.uuid)" [class.border-rose-100]="!seleccionados().has(p.uuid)">
        <div class="flex flex-col items-center gap-2 shrink-0">
          <input type="checkbox" [checked]="seleccionados().has(p.uuid)" (change)="toggle(p)" [disabled]="!p.activado && !p.puedeActivarWeb" [title]="p.motivoBloqueo || ''" class="accent-rose-500 w-4 h-4 disabled:opacity-30" />
          <div class="w-20 h-20 rounded-2xl overflow-hidden bg-[#fff5f7]">
            <img *ngIf="p.urlImagen" [src]="p.urlImagen" [alt]="p.nombre" class="w-full h-full object-cover" />
            <app-ui-empty-image *ngIf="!p.urlImagen"></app-ui-empty-image>
          </div>
        </div>
        <div class="min-w-0 flex-1 flex flex-col">
          <div class="flex items-center gap-2">
            <span class="text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded" [class.bg-sky-50]="p.tipo === 'COMBO'" [class.text-sky-600]="p.tipo === 'COMBO'" [class.bg-violet-50]="p.tipo !== 'COMBO'" [class.text-violet-600]="p.tipo !== 'COMBO'">
              {{ p.tipo === 'COMBO' ? 'Combo' : 'Producto' }}
            </span>
            <span *ngIf="p.activado" class="text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-600">Activado</span>
            <span *ngIf="!p.activado && p.puedeActivarWeb" class="text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-50 text-amber-600">Sin activar</span>
            <span *ngIf="!p.activado && !p.puedeActivarWeb" class="text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded bg-red-50 text-red-500">{{ p.motivoBloqueo }}</span>
          </div>
          <p class="font-bold text-gray-800 leading-snug line-clamp-2 mt-1">{{ p.nombre }}</p>
          <p class="text-xs text-gray-400 mt-0.5">{{ p.sku }}<span *ngIf="p.slug"> · {{ p.slug }}</span></p>
          <p class="text-sm font-black text-rose-500 mt-1">S/ {{ p.precioFinal.toFixed(2) }}</p>
          <div class="mt-auto pt-2 flex items-center justify-between">
            <span *ngIf="p.activado" class="text-[10px] font-bold uppercase tracking-wide px-2 py-1 rounded-full" [class.bg-emerald-50]="p.visibleCatalogo" [class.text-emerald-600]="p.visibleCatalogo" [class.bg-gray-100]="!p.visibleCatalogo" [class.text-gray-500]="!p.visibleCatalogo">
              {{ p.visibleCatalogo ? 'Visible' : 'Oculto' }}
            </span>
            <span *ngIf="!p.activado" class="text-[10px] text-gray-300">—</span>
            <div class="flex gap-3 text-xs font-bold">
              <button *ngIf="!p.activado && p.puedeActivarWeb" type="button" (click)="activar(p)" class="text-rose-500">Activar web</button>
              <span *ngIf="!p.activado && !p.puedeActivarWeb" class="text-gray-300">No activable</span>
              <ng-container *ngIf="p.activado">
                <a [routerLink]="['/admin/productos', p.uuid]" class="text-rose-500">Configurar</a>
                <ng-container *ngIf="confirmarDesactivar() !== p.uuid">
                  <button type="button" (click)="confirmarDesactivar.set(p.uuid)" class="text-gray-400 hover:text-red-500">Desactivar</button>
                </ng-container>
                <ng-container *ngIf="confirmarDesactivar() === p.uuid">
                  <span class="text-red-500">¿Quitar de la web?</span>
                  <button type="button" (click)="desactivar(p)" class="text-red-500 hover:underline">Sí</button>
                  <button type="button" (click)="confirmarDesactivar.set(null)" class="text-gray-400 hover:underline">No</button>
                </ng-container>
              </ng-container>
            </div>
          </div>
        </div>
      </article>
    </div>

    <div *ngIf="!loading() && total() > 0" class="mt-6 flex flex-wrap items-center justify-between gap-3">
      <p class="text-sm text-gray-400">Página {{ page() }} de {{ totalPaginas() }} · {{ total() }} productos</p>
      <div class="flex gap-2">
        <button type="button" (click)="irPagina(page() - 1)" [disabled]="page() <= 1" class="px-4 py-2 rounded-xl text-sm font-bold border border-gray-200 bg-white text-gray-600 disabled:opacity-40">Anterior</button>
        <button type="button" (click)="irPagina(page() + 1)" [disabled]="page() >= totalPaginas()" class="px-4 py-2 rounded-xl text-sm font-bold border border-gray-200 bg-white text-gray-600 disabled:opacity-40">Siguiente</button>
      </div>
    </div>
    `
})
export class AdminProductosComponent implements OnInit {
    private productoService = inject(ProductoService);
    private alerts = inject(AlertService);

    productos = signal<ProductoListApi[]>([]);
    total = signal(0);
    page = signal(1);
    pageSize = signal(24);
    busqueda = signal('');
    estado = signal<'todos' | 'activados' | 'sin'>('todos');
    tipo = signal<'todos' | 'VARIANTE' | 'COMBO'>('todos');
    seleccionados = signal<Map<string, { tipo: string; id: string }>>(new Map());
    loading = signal(true);
    procesando = signal(false);

    totalPaginas = computed(() => Math.max(1, Math.ceil(this.total() / this.pageSize())));
    paginaCompletaSeleccionada = computed(() => {
        const items = this.productos();
        return items.length > 0 && items.every((p) => this.seleccionados().has(p.uuid));
    });

    private searchTimer: ReturnType<typeof setTimeout> | null = null;

    ngOnInit(): void {
        this.reload();
    }

    onSearch(value: string) {
        this.busqueda.set(value);
        if (this.searchTimer) {
            clearTimeout(this.searchTimer);
        }
        this.searchTimer = setTimeout(() => {
            this.page.set(1);
            this.reload();
        }, 400);
    }

    onEstado(value: 'todos' | 'activados' | 'sin') {
        this.estado.set(value);
        this.page.set(1);
        this.reload();
    }

    onTipo(value: 'todos' | 'VARIANTE' | 'COMBO') {
        this.tipo.set(value);
        this.page.set(1);
        this.reload();
    }

    onPageSize(value: number) {
        this.pageSize.set(Number(value));
        this.page.set(1);
        this.reload();
    }

    irPagina(value: number) {
        if (value < 1 || value > this.totalPaginas()) {
            return;
        }
        this.page.set(value);
        this.reload();
    }

    reload() {
        this.loading.set(true);
        this.productoService.listProductos({
            key: this.busqueda().trim() || null,
            tipo: this.tipo() === 'todos' ? null : this.tipo(),
            activado: this.estado() === 'todos' ? null : this.estado() === 'activados',
            pageNumber: this.page(),
            pageSize: this.pageSize(),
            incluirOcultos: true
        }).subscribe({
            next: (result) => {
                this.productos.set(result.data ?? []);
                this.total.set(result.total ?? 0);
                this.loading.set(false);
            },
            error: () => {
                this.loading.set(false);
                this.alerts.error('No se pudieron cargar los productos.');
            }
        });
    }

    toggle(p: ProductoListApi) {
        if (!p.activado && !p.puedeActivarWeb) {
            this.alerts.error(`${p.nombre}: ${p.motivoBloqueo ?? 'No se puede activar'}`);
            return;
        }

        const next = new Map(this.seleccionados());
        if (next.has(p.uuid)) {
            next.delete(p.uuid);
        } else {
            next.set(p.uuid, { tipo: p.tipo, id: p.uuid });
        }
        this.seleccionados.set(next);
    }

    togglePagina(marcar: boolean) {
        const next = new Map(this.seleccionados());
        for (const item of this.productos()) {
            if (marcar) {
                if (item.activado || item.puedeActivarWeb) {
                    next.set(item.uuid, { tipo: item.tipo, id: item.uuid });
                }
            } else {
                next.delete(item.uuid);
            }
        }
        this.seleccionados.set(next);
    }

    limpiarSeleccion() {
        this.seleccionados.set(new Map());
    }

    activar(p: ProductoListApi) {
        this.productoService.validarProductoWeb({ tipo: p.tipo, id: p.uuid }).subscribe({
            next: (validacion) => {
                if (!validacion) {
                    this.alerts.error('No se pudo validar el producto.');
                    return;
                }
                if (!validacion.puedeActivar) {
                    this.alerts.error(validacion.errores.join(' '));
                    return;
                }

                this.productoService.activarProducto({ tipo: p.tipo, id: p.uuid }).subscribe({
                    next: (ok) => {
                        if (ok) {
                            const avisos = validacion.advertencias.length > 0 ? ' Avisos: ' + validacion.advertencias.join(' ') : '';
                            this.alerts.ok('Producto activado para web.' + avisos);
                            this.reload();
                        } else {
                            this.alerts.error('No se pudo activar el producto.');
                        }
                    },
                    error: () => this.alerts.error('No se pudo activar el producto.')
                });
            },
            error: () => this.alerts.error('No se pudo validar el producto.')
        });
    }

    confirmarDesactivar = signal<string | null>(null);
    confirmarBulk = signal(false);

    desactivar(p: ProductoListApi) {
        this.confirmarDesactivar.set(null);
        this.productoService.desactivarProducto({ tipo: p.tipo, id: p.uuid }).subscribe({
            next: (ok) => {
                if (ok) {
                    this.alerts.ok('Producto desactivado de la web.');
                    this.reload();
                } else {
                    this.alerts.error('No se pudo desactivar.');
                }
            },
            error: () => this.alerts.error('No se pudo desactivar.')
        });
    }

    activarSeleccionados() {
        const items = this.itemsSeleccionados();
        if (items.length === 0) {
            return;
        }

        this.procesando.set(true);
        this.productoService.activarProductos(items).subscribe({
            next: (result) => {
                this.procesando.set(false);
                if (!result) {
                    this.alerts.error('No se pudo procesar la selección.');
                    return;
                }
                const errores = result.errores.length > 0 ? ` Errores: ${result.errores.slice(0, 3).join(' | ')}` : '';
                this.alerts.ok(`${result.procesados} activados, ${result.omitidos} omitidos.${errores}`);
                this.limpiarSeleccion();
                this.reload();
            },
            error: () => {
                this.procesando.set(false);
                this.alerts.error('No se pudo procesar la selección.');
            }
        });
    }

    desactivarSeleccionados() {
        this.confirmarBulk.set(false);
        const items = this.itemsSeleccionados();
        if (items.length === 0) {
            return;
        }

        this.procesando.set(true);
        this.productoService.desactivarProductos(items).subscribe({
            next: (result) => {
                this.procesando.set(false);
                if (!result) {
                    this.alerts.error('No se pudo procesar la selección.');
                    return;
                }
                this.alerts.ok(`${result.procesados} desactivados.`);
                this.limpiarSeleccion();
                this.reload();
            },
            error: () => {
                this.procesando.set(false);
                this.alerts.error('No se pudo procesar la selección.');
            }
        });
    }

    private itemsSeleccionados() {
        return Array.from(this.seleccionados().values());
    }
}
